import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';

const GOLD   = '#A9B2B9';
const GOLD2  = '#a8872e';
const DARK1  = '#0a0a0a';
const DARK2  = '#141414';

const PRIZES = [
  { id: 1, label: 'Δοκίμασε ξανά',   probability: 88   },
  { id: 2, label: '50 Coins',        probability: 4    },
  { id: 3, label: '100 Coins',       probability: 3    },
  { id: 4, label: '200 Coins',       probability: 2    },
  { id: 5, label: 'Έκπτωση 20%',     probability: 1    },
  { id: 6, label: 'Δωρεάν Μούσι',    probability: 1.9  },
  { id: 7, label: 'Δωρεάν Κούρεμα',  probability: 0.1  },
];

const N       = PRIZES.length;
const SEG_RAD = (2 * Math.PI) / N;

function drawWheel(canvas, rotDeg) {
  const dpr  = window.devicePixelRatio || 2;
  const size = parseInt(canvas.style.width || canvas.width);
  // Scale canvas for sharp HiDPI rendering
  if (canvas.width !== size * dpr) {
    canvas.width  = size * dpr;
    canvas.height = size * dpr;
  }
  const ctx  = canvas.getContext('2d');
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const W    = size;
  const cx   = W / 2, cy = W / 2;
  const R    = W / 2 - 4;
  const capR = W * 0.082;
  const rot  = (rotDeg * Math.PI) / 180 - Math.PI / 2;

  ctx.clearRect(0, 0, W, W);

  // Outer glow ring
  const grad = ctx.createRadialGradient(cx, cy, R - 8, cx, cy, R + 4);
  grad.addColorStop(0, 'rgba(201,168,76,0.12)');
  grad.addColorStop(1, 'rgba(201,168,76,0)');
  ctx.beginPath();
  ctx.arc(cx, cy, R + 4, 0, 2 * Math.PI);
  ctx.fillStyle = grad;
  ctx.fill();

  // Draw segments
  for (let i = 0; i < N; i++) {
    const a1  = rot + i * SEG_RAD;
    const a2  = rot + (i + 1) * SEG_RAD;
    const mid = rot + (i + 0.5) * SEG_RAD;
    const tR  = R * 0.62;

    // Alternating dark fills
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, R, a1, a2);
    ctx.closePath();
    ctx.fillStyle = i % 2 === 0 ? DARK2 : DARK1;
    ctx.fill();

    // Gold divider lines
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + R * Math.cos(a1), cy + R * Math.sin(a1));
    ctx.strokeStyle = GOLD2;
    ctx.lineWidth   = 1;
    ctx.globalAlpha = 0.5;
    ctx.stroke();
    ctx.restore();

    // Gold text
    ctx.save();
    ctx.translate(cx + tR * Math.cos(mid), cy + tR * Math.sin(mid));
    ctx.rotate(mid + Math.PI / 2);
    ctx.fillStyle    = GOLD;
    ctx.font         = `500 12px "Helvetica Neue", Helvetica, Arial, sans-serif`;
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'middle';
    ctx.imageSmoothingEnabled = true;

    // Split long labels across 2 lines
    const words = PRIZES[i].label.split(' ');
    if (words.length >= 2) {
      const mid1 = Math.ceil(words.length / 2);
      const l1   = words.slice(0, mid1).join(' ');
      const l2   = words.slice(mid1).join(' ');
      ctx.fillText(l1, 0, -6);
      ctx.fillText(l2, 0,  6);
    } else {
      ctx.fillText(PRIZES[i].label, 0, 0);
    }
    ctx.restore();
  }

  // Outer ring (gold)
  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, 2 * Math.PI);
  ctx.strokeStyle = GOLD2;
  ctx.lineWidth   = 1.5;
  ctx.globalAlpha = 0.8;
  ctx.stroke();
  ctx.globalAlpha = 1;

  // Center cap
  ctx.beginPath();
  ctx.arc(cx, cy, capR, 0, 2 * Math.PI);
  ctx.fillStyle = '#000';
  ctx.fill();
  ctx.strokeStyle = GOLD2;
  ctx.lineWidth   = 1.5;
  ctx.stroke();

  // Center dot (gold)
  ctx.beginPath();
  ctx.arc(cx, cy, 5, 0, 2 * Math.PI);
  ctx.fillStyle = GOLD;
  ctx.fill();
}

/* ── Component ─────────────────────────────────────── */
function SpinWheelInner({ onClose, onSpin, newBalance }) {
  const [spinning, setSpinning]     = useState(false);
  const [rotation, setRotation]     = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [prize, setPrize]           = useState(null);
  const canvasRef                   = useRef(null);
  const animRef                     = useRef(null);

  useEffect(() => {
    if (canvasRef.current) drawWheel(canvasRef.current, rotation);
  }, [rotation]);

  const selectPrize = () => {
    const rand = Math.random() * 100;
    let cum = 0;
    for (const p of PRIZES) {
      cum += p.probability;
      if (rand <= cum) return p;
    }
    return PRIZES[0];
  };

  const handleSpin = async () => {
    if (spinning) return;
    setSpinning(true);
    const selected = selectPrize();
    const idx      = PRIZES.indexOf(selected);
    const segMid   = (idx + 0.5) * (360 / N);
    const target   = 1800 + (360 - ((rotation % 360) + segMid) % 360);
    const finalRot = rotation + target;

    const dur   = 4200;
    const start = performance.now();
    const from  = rotation;

    function ease(t) { return 1 - Math.pow(1 - t, 4); }

    function frame(now) {
      const t    = Math.min((now - start) / dur, 1);
      const curr = from + target * ease(t);
      setRotation(curr);
      if (t < 1) {
        animRef.current = requestAnimationFrame(frame);
      } else {
        setRotation(finalRot);
        setSpinning(false);
        setPrize(selected);
        // fire and forget — catch to avoid unhandled rejection
        Promise.resolve(onSpin(selected)).catch(() => {});
        setTimeout(() => setShowResult(true), 400);
      }
    }
    animRef.current = requestAnimationFrame(frame);
  };

  useEffect(() => () => { if (animRef.current) cancelAnimationFrame(animRef.current); }, []);

  const isWin = prize && prize.id !== 1;
  const SIZE  = 300;

  const ui = (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.97)',
      fontFamily: 'Helvetica Neue, Arial, sans-serif',
    }}>
      {/* Close */}
      <button
        onClick={onClose}
        disabled={spinning || (!showResult && prize !== null)}
        data-testid="spin-wheel-close"
        style={{ 
          position: 'fixed', 
          top: 20, 
          right: 20, 
          background: 'none', 
          border: 'none', 
          cursor: (spinning || (!showResult && prize !== null)) ? 'not-allowed' : 'pointer', 
          color: (spinning || (!showResult && prize !== null)) ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.25)', 
          opacity: (spinning || (!showResult && prize !== null)) ? 0.3 : 1,
          transition: 'all 0.3s',
          zIndex: 2 
        }}
      >
        <X size={18} />
      </button>

      {!showResult ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 }}>

          {/* Header */}
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: 9, letterSpacing: '0.3em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)', margin: '0 0 6px' }}>Elite Loyalty</p>
            <h2 style={{ fontSize: 18, fontWeight: 300, letterSpacing: '0.22em', color: '#fff', textTransform: 'uppercase', margin: 0 }}>Τροχός Τύχης</h2>
            <div style={{ width: 24, height: 1, background: `${GOLD}44`, margin: '8px auto 0' }} />
          </div>

          {/* Wheel */}
          <div style={{ position: 'relative' }}>
            {/* Gold pointer */}
            <div style={{
              position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
              width: 0, height: 0,
              borderLeft: '7px solid transparent',
              borderRight: '7px solid transparent',
              borderTop: `17px solid ${GOLD}`,
              filter: `drop-shadow(0 2px 6px ${GOLD}66)`,
              zIndex: 2,
            }} />
            <canvas
              ref={canvasRef}
              width={SIZE}
              height={SIZE}
              style={{ display: 'block', borderRadius: '50%', width: SIZE, height: SIZE }}
            />
          </div>

          {/* Spin button */}
          <button
            onClick={handleSpin}
            disabled={spinning}
            data-testid="spin-button"
            style={{
              padding: '11px 52px',
              fontSize: 10,
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
              fontWeight: 400,
              background: 'transparent',
              color: spinning ? 'rgba(201,168,76,0.4)' : GOLD,
              border: `1px solid ${spinning ? 'rgba(201,168,76,0.2)' : GOLD2}`,
              cursor: spinning ? 'default' : 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => { if (!spinning) { e.currentTarget.style.background = `${GOLD}18`; } }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            {spinning ? 'Γυρίζει...' : 'Γύρισε'}
          </button>

          <p style={{ fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.15)', margin: 0, textAlign: 'center' }}>
            Ενεργοποιείται με κάθε νέο ραντεβού
          </p>
        </div>
      ) : (
        /* Result */
        <div
          data-testid="spin-result"
          style={{
            maxWidth: 300, width: '90%', textAlign: 'center',
            border: `1px solid ${isWin ? GOLD2 : '#222'}`,
            background: '#080808',
            padding: '44px 28px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
            <div style={{ flex: 1, height: 1, background: `${GOLD}22` }} />
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: isWin ? GOLD : '#333' }} />
            <div style={{ flex: 1, height: 1, background: `${GOLD}22` }} />
          </div>

          <p style={{ fontSize: 9, letterSpacing: '0.3em', textTransform: 'uppercase', color: `${GOLD}80`, marginBottom: 14 }}>
            {isWin ? 'Συγχαρητήρια' : 'Αποτέλεσμα'}
          </p>
          <h3 style={{ fontSize: 17, fontWeight: 300, letterSpacing: '0.12em', textTransform: 'uppercase', color: isWin ? GOLD : '#444', marginBottom: 20 }}>
            {prize?.label}
          </h3>

          {isWin && (
            <p style={{ fontSize: 12, fontWeight: 300, color: 'rgba(255,255,255,0.35)', lineHeight: 1.7, marginBottom: newBalance !== null && newBalance !== undefined ? 12 : 28 }}>
              {prize.id === 2 && '+50 coins. Προστέθηκαν αυτόματα.'}
              {prize.id === 3 && '+100 coins. Προστέθηκαν αυτόματα.'}
              {prize.id === 4 && '+200 coins. Προστέθηκαν αυτόματα.'}
              {prize.id === 5 && 'Κέρδισες 20% έκπτωση. Ενημέρωσε τον barber σου.'}
              {prize.id === 6 && 'Κέρδισες δωρεάν μούσι. Ενημέρωσε τον barber σου.'}
              {prize.id === 7 && 'JACKPOT — Δωρεάν κούρεμα! Ενημέρωσε τον barber σου αμέσως.'}
            </p>
          )}
          {/* Show updated balance if coins were awarded */}
          {isWin && newBalance !== null && newBalance !== undefined && [2,3,4].includes(prize?.id) && (
            <div style={{
              background: `${GOLD}12`,
              border: `1px solid ${GOLD}30`,
              borderRadius: 6,
              padding: '8px 14px',
              marginBottom: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}>
              <span style={{ fontSize: 10, color: `${GOLD}80`, letterSpacing: '0.2em', textTransform: 'uppercase' }}>Νέο υπόλοιπο</span>
              <span style={{ fontSize: 15, fontWeight: 300, color: GOLD }}>{newBalance.toLocaleString()}</span>
              <span style={{ fontSize: 10, color: `${GOLD}60` }}>coins</span>
            </div>
          )}
          {!isWin && (
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.2)', marginBottom: 28 }}>
              Δοκίμασε ξανά στο επόμενο ραντεβού σου.
            </p>
          )}

          <button
            onClick={() => {
              onClose();
            }}
            data-testid="spin-result-close"
            onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD2; e.currentTarget.style.color = GOLD; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#333'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
            style={{
              width: '100%', padding: '11px', fontSize: 10,
              letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 400,
              background: 'transparent', color: 'rgba(255,255,255,0.5)',
              border: '1px solid #333', cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            Κλείσιμο
          </button>
        </div>
      )}
    </div>
  );

  return ReactDOM.createPortal(ui, document.body);
}

export default function SpinWheel(props) {
  return <SpinWheelInner {...props} />;
}
